import * as React from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router'
import { Text, StackChildren, Stack } from '@doordash/design-language'
import Button from '@doordash/component-button'
import { Interpolate } from '@doordash/lib-i18n'
import Loading from '@doordash/component-loading'
import { isEmpty } from 'lodash'

import { i18nKeys } from 'translationTypes'
import i18n from '../../../i18n'
import * as actions from '../../../actions'
import * as events from '../../../events'
import { GetFirstDashCampaignsSuccessPayload } from '../../../api'

import PromotionalCards from '../../molecules/promotional-cards'
import LinkButton from '../../atoms/link-button'
import routes from '../../../routes'
import { Gift, BonusTag } from '../../atoms/icons'
import {
  mapCampaignsToPromotionalCards,
  getHourlyPayFromPromotionalCards,
  shouldShowHourlyPay,
} from './util'
import Page from '../../molecules/page'
import PageRoot from '../../molecules/page-root'
import Simple from '../../templates/simple'
import { SimpleStyleType } from '../../templates/simple/constants'
import { Root } from '../../pages/onboarding/styles'
import * as DA from '../../../typeModules/events'
import * as Exp from '../../../experimentConstants'
import t from './strings'
import { CardContent, CardTypes } from '../../molecules/promotional-cards/Card'
import { GetStartedPromotions as Tag } from '../../../models/selectorAnchors'
import { isFeatureFlagEnabled } from '@doordash/lib-feature-flags'

type GetStartedPromotionsProps = {
  firstDashCampaigns?: GetFirstDashCampaignsSuccessPayload[]
  getPromotionalCards?: (
    uniqueLink: string
  ) => Promise<GetFirstDashCampaignsSuccessPayload[]>
  clearErrors?: () => Promise<void>
  errorMessage?: string
  loadDasher?: () => Promise<void>
  applicantUniqueLink?: string
  track?: (
    action: DA.EventActions,
    name: DA.PromotionsPageNames,
    data?: string
  ) => Promise<void>
}

export const DEFAULT_PROMOTIONAL_CARD: CardContent = {
  title: t.defaultCardTitle(),
  content: t.defaultCardContent(),
  icon: <Gift />,
  type: CardTypes.Default,
}

export const SECONDARY_DEFAULT_PROMOTIONAL_CARD: CardContent = {
  title: t.defaultSecondaryCardTitle(),
  content: t.defaultSecondaryCardContent(),
  icon: <BonusTag />,
  type: CardTypes.Static,
}

export const GetStartedPromotions: React.FC<GetStartedPromotionsProps> = ({
  firstDashCampaigns,
  getPromotionalCards,
  loadDasher,
  applicantUniqueLink,
  track,
}) => {
  const [isLoading, setIsLoading] = React.useState<boolean>(true)
  const [promotionalCards, setPromotionalCards] = React.useState<CardContent[]>(
    null
  )
  const [promotionUnitAmount, setPromotionUnitAmount] = React.useState<number>()
  const [hourlyPayDisplayString, setHourlyPayDisplayString] = React.useState<
    string
  >()

  React.useEffect(() => {
    // Track GetStartedPromotions render
    track(DA.EventActions.Rendered, DA.PromotionsPageNames.GetStarted)
  }, [track])

  /**
   * Loads Dasher data in case the user hits this route/page directly
   * insteading of routing from elsewhere
   */
  React.useEffect(() => {
    const loadApplicantData = async () => {
      if (applicantUniqueLink === null) {
        await loadDasher()
      }
    }

    loadApplicantData()
  }, [applicantUniqueLink, loadDasher])

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        await getPromotionalCards(applicantUniqueLink)
      } catch (e) {
        setPromotionalCards([DEFAULT_PROMOTIONAL_CARD])
      } finally {
        setIsLoading(false)
      }
    }

    if (applicantUniqueLink) {
      fetchData()
    }
  }, [getPromotionalCards, applicantUniqueLink])

  React.useEffect(() => {
    if (!isEmpty(firstDashCampaigns)) {
      const cards = mapCampaignsToPromotionalCards(firstDashCampaigns, [
        CardTypes.HourlyPay,
      ])

      // Add an additional default card at the end, if live promotional campaigns are retrieve successfully
      setPromotionalCards([...cards, SECONDARY_DEFAULT_PROMOTIONAL_CARD])

      const { unitAmount, displayString } = getHourlyPayFromPromotionalCards(
        firstDashCampaigns
      )

      setPromotionUnitAmount(unitAmount)
      setHourlyPayDisplayString(displayString)

      // Track what hourly pay users are seeing
      if (shouldShowHourlyPay(unitAmount)) {
        track(
          DA.EventActions.Rendered,
          DA.PromotionsPageNames.HourlyPay,
          displayString
        )
      }
    }
  }, [firstDashCampaigns, track])

  const getHourlyPay = (): GetFirstDashCampaignsSuccessPayload => {
    return Object.values(firstDashCampaigns).find(
      campaign => campaign.type === CardTypes.HourlyPay
    )
  }

  const getPromotionsTitle = () => {
    if (!isEmpty(firstDashCampaigns)) {
      const hourlyPay = getHourlyPay()
      if (shouldShowHourlyPay(promotionUnitAmount)) {
        if (hourlyPay.extras && hourlyPay.extras.submarketName) {
          return i18nKeys.excitement__get_started__title_submarket
        }

        return i18nKeys.excitement__get_started__title
      }
    }

    return i18nKeys.excitement__get_started__title_default
  }

  return (
    <StackChildren>
      {getPromotionsTitle() ===
      i18nKeys.excitement__get_started__title_submarket ? (
        <Text styles={Text.Styles.PageTitle2}>
          <Interpolate
            i18n={i18n}
            i18nKey={getPromotionsTitle()}
            values={{
              submarketName: getHourlyPay().extras.submarketName,
              dollars: hourlyPayDisplayString,
            }}
            components={[
              <Text
                display={Text.DisplayModes.Inline}
                styles={Text.Styles.PageTitle2}
                color={Text.Colors.TextAccentedSecondary}
                key={t.titleSubmarket()}
              />,
              <Text
                display={Text.DisplayModes.Inline}
                styles={Text.Styles.PageTitle2}
                color={Text.Colors.TextAccentedSecondary}
                key={t.titleSubmarket()}
              />,
            ]}
            data-anchor-id={Tag.Title}
          />
        </Text>
      ) : (
        <Text styles={Text.Styles.PageTitle2}>
          <Interpolate
            i18n={i18n}
            i18nKey={getPromotionsTitle()}
            values={{ dollars: hourlyPayDisplayString }}
            components={[
              <Text
                display={Text.DisplayModes.Inline}
                styles={Text.Styles.PageTitle2}
                color={Text.Colors.TextAccentedSecondary}
                key={t.title()}
              />,
            ]}
            data-anchor-id={Tag.Title}
          />
        </Text>
      )}

      <Text styles={Text.Styles.Body1} color={Text.Colors.TextSecondary}>
        {t.titleSecondary()}
      </Text>
      <Stack size={Stack.Sizes.XLarge} />
      <Text styles={Text.Styles.Label1Emphasis}>{t.promotionsTitle()}</Text>

      {isLoading ? <Loading /> : <PromotionalCards cards={promotionalCards} />}

      <Stack size={Stack.Sizes.XLarge} />
      <Text styles={Text.Styles.Caption2} color={Text.Colors.TextTertiary}>
        {t.legalDisclaimer()}
      </Text>
      <Stack size={Stack.Sizes.XLarge} />
      {isFeatureFlagEnabled(Exp.DX_DIRECT) ? (
        <Button
          type={Button.Types.PrimaryPill}
          size={Button.Sizes.Large}
          onClick={e => {
            e.stopPropagation()
            track(DA.EventActions.Clicked, DA.PromotionsPageNames.ChooseHowToGetPaid)
            window.location.assign(routes.paymentAccountLandingPage.path)
          }}
          data-anchor-id={Tag.Continue}
        >
          {t.chooseHowToGetPaid()}
        </Button>
      ) : (
        <LinkButton
          type={Button.Types.PrimaryPill}
          size={Button.Sizes.Large}
          onClick={e => {
            e.stopPropagation()
            track(DA.EventActions.Clicked, DA.PromotionsPageNames.GetStarted)
          }}
          href={routes.promotionsBankAccount.path}
          data-anchor-id={Tag.Continue}
        >
          {t.primaryAction()}
        </LinkButton>
      )}
    </StackChildren>
  )
}

const mapStateToProps = ({
  errorMessage,
  firstDashCampaigns,
  applicantUniqueLink,
}) => ({
  errorMessage,
  firstDashCampaigns,
  applicantUniqueLink,
})

const mapDispatchToProps = {
  getPromotionalCards: actions.getPromotionalCards,
  clearErrors: actions.clearErrors,
  loadDasher: actions.loadDasher,
  track: events.trackPromotionsPage,
}

const ConnectedGetStartedPromotions = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(GetStartedPromotions)
)

export const WrappedGetStartedPromotions: React.FC<GetStartedPromotionsProps> = ({
  errorMessage,
  clearErrors,
  ...props
}) => (
  <Simple
    includeFooter={false}
    headerIsDark={false}
    clearErrors={clearErrors}
    errorMessage={errorMessage}
    type={SimpleStyleType.Standard}
  >
    <Root>
      <Page>
        <PageRoot>
          <ConnectedGetStartedPromotions {...props} />
        </PageRoot>
      </Page>
    </Root>
  </Simple>
)

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(GetStartedPromotions)
